import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import usePrivateLink from "../config/usePrivateLink";

export default function HostelHeader() {
  const hasAccess = usePrivateLink();
  const location = useLocation();
  const pathname = location.pathname;

  /*
   * Sidebar:
   * false = collapsed / icon only
   * true  = expanded / icon + label
   *
   * The sidebar starts collapsed unless the user previously
   * expanded it.
   */
  const [isExpanded, setIsExpanded] = useState(() => {
    return localStorage.getItem("hostelSidebarExpanded") === "true";
  });

  useEffect(() => {
    localStorage.setItem("hostelSidebarExpanded", String(isExpanded));

    const root = document.documentElement;

    const baseClasses = [
      "light-style",
      "layout-navbar-fixed",
      "layout-compact",
      "layout-menu-fixed",
    ];

    if (!isExpanded) {
      baseClasses.push("layout-menu-collapsed");
    }

    root.className = baseClasses.join(" ");
  }, [isExpanded]);

  const toggleSidebar = () => {
    setIsExpanded((prev) => !prev);
  };

  /*
   * On smaller screens, selecting a menu item collapses
   * the sidebar so the content gets the full available width.
   */
  const handleMenuClick = () => {
    if (window.innerWidth < 1200) {
      setIsExpanded(false);
    }
  };

  const isActive = (...paths) => paths.includes(pathname);

  return (
    <>
      <style>{`
        /* =========================================================
           HOSTEL SIDEBAR
        ========================================================= */

        #layout-menu.hostel-sidebar {
          width: 260px;
          max-height: 100vh;

          overflow-x: hidden;
          overflow-y: auto;

          background: var(--bs-menu-bg, var(--bs-body-bg));

          border-right: 1px solid var(--bs-border-color);

          box-shadow:
            2px 0 12px rgba(0, 0, 0, 0.04);

          transition:
            width 0.22s ease,
            box-shadow 0.22s ease;
        }

        /* Collapsed desktop width */
        html.layout-menu-collapsed
          #layout-menu.hostel-sidebar {
          width: 80px;
        }


        /* =========================================================
           SIDEBAR TOP / BRAND

           No school logo here intentionally.
           The school logo/name is already visible in the main
           application header. The sidebar is kept as navigation.
        ========================================================= */

        #layout-menu.hostel-sidebar .app-brand {
          height: 72px;

          display: flex;
          align-items: center;
          justify-content: space-between;

          padding: 0 16px;

          border-bottom: 1px solid var(--bs-border-color);

          overflow: hidden;

          position: relative;
        }

        .hostel-brand {
          min-width: 0;

          display: flex;
          align-items: center;

          gap: 10px;

          color: var(--bs-body-color);

          text-decoration: none;
        }

        .hostel-brand-icon {
          width: 38px;
          height: 38px;
          min-width: 38px;

          display: flex;
          align-items: center;
          justify-content: center;

          border-radius: 9px;

          color: var(--bs-primary);

          background: rgba(
            var(--bs-primary-rgb),
            0.10
          );

          font-size: 20px;
        }

        .hostel-brand-text {
          min-width: 0;

          display: flex;
          flex-direction: column;
        }

        .hostel-brand-title {
          color: var(--bs-body-color);

          font-size: 14px;
          font-weight: 700;

          line-height: 1.2;

          white-space: nowrap;
        }

        .hostel-brand-subtitle {
          margin-top: 2px;

          color: var(--bs-secondary-color);

          font-size: 9px;
          font-weight: 500;

          line-height: 1.2;

          white-space: nowrap;
        }


        /* =========================================================
           TOGGLE BUTTON
        ========================================================= */

        .hostel-toggle-btn {
          width: 34px;
          height: 34px;

          min-width: 34px;

          display: inline-flex;
          align-items: center;
          justify-content: center;

          padding: 0;

          border: 1px solid var(--bs-border-color);

          border-radius: 8px;

          background: var(--bs-body-bg);

          color: var(--bs-secondary-color);

          cursor: pointer;

          transition:
            color 0.15s ease,
            background-color 0.15s ease,
            border-color 0.15s ease,
            transform 0.2s ease;
        }

        .hostel-toggle-btn:hover {
          color: var(--bs-primary);

          border-color: rgba(
            var(--bs-primary-rgb),
            0.35
          );

          background: rgba(
            var(--bs-primary-rgb),
            0.08
          );
        }

        .hostel-toggle-btn i {
          font-size: 19px;

          transition: transform 0.2s ease;
        }

        /*
         * When expanded, arrow points left.
         * When collapsed, arrow points right.
         */
        html.layout-menu-collapsed
          #layout-menu.hostel-sidebar
          .hostel-toggle-btn i {
          transform: rotate(180deg);
        }


        /* =========================================================
           COLLAPSED BRAND
        ========================================================= */

        html.layout-menu-collapsed
          #layout-menu.hostel-sidebar
          .app-brand {
          justify-content: center;

          padding: 0 6px;
        }

        html.layout-menu-collapsed
          #layout-menu.hostel-sidebar
          .hostel-brand-text {
          display: none;
        }

        html.layout-menu-collapsed
          #layout-menu.hostel-sidebar
          .hostel-brand {
          gap: 0;
        }

        html.layout-menu-collapsed
          #layout-menu.hostel-sidebar
          .hostel-brand-icon {
          width: 40px;
          height: 40px;
          min-width: 40px;

          font-size: 21px;
        }

        html.layout-menu-collapsed
          #layout-menu.hostel-sidebar
          .hostel-toggle-btn {
          position: absolute;

          right: 5px;
          top: 19px;

          width: 24px;
          min-width: 24px;

          height: 32px;

          border: 0;

          background: transparent;
        }


        /* =========================================================
           MENU
        ========================================================= */

        #layout-menu.hostel-sidebar .menu-inner {
          padding-top: 12px !important;
          padding-bottom: 12px !important;
        }

        #layout-menu.hostel-sidebar .menu-item {
          margin: 3px 8px;
        }

        #layout-menu.hostel-sidebar .menu-link {
          min-height: 45px;

          display: flex;
          align-items: center;

          position: relative;

          border-radius: 10px;

          color: var(--bs-body-color);

          transition:
            background-color 0.15s ease,
            color 0.15s ease,
            box-shadow 0.15s ease;
        }

        /* Normal hover */
        #layout-menu.hostel-sidebar
          .menu-link:hover {
          color: var(--bs-primary);

          background: rgba(
            var(--bs-primary-rgb),
            0.07
          );
        }

        /*
         * ACTIVE MENU
         *
         * Main theme color is used as requested.
         * This gives a clear selected state without using
         * the previous pale purple-only highlight.
         */
        #layout-menu.hostel-sidebar
          .menu-item.active
          > .menu-link {
          color: #ffffff !important;

          background: var(--bs-primary);

          font-weight: 600;

          box-shadow:
            0 5px 12px rgba(
              var(--bs-primary-rgb),
              0.24
            );
        }

        #layout-menu.hostel-sidebar
          .menu-item.active
          > .menu-link
          .menu-icon {
          color: #ffffff !important;
        }

        /* =========================================================
           ICONS
        ========================================================= */

        #layout-menu.hostel-sidebar .menu-icon {
          width: 25px;
          min-width: 25px;

          margin-right: 11px !important;

          display: inline-flex;
          align-items: center;
          justify-content: center;

          color: inherit;

          font-size: 19px;
        }

        #layout-menu.hostel-sidebar
          .menu-link
          > div {
          white-space: nowrap;
        }

        /*
         * Collapsed menu = clean icon rail.
         */
        html.layout-menu-collapsed
          #layout-menu.hostel-sidebar
          .menu-item {
          margin-left: 8px;
          margin-right: 8px;
        }

        html.layout-menu-collapsed
          #layout-menu.hostel-sidebar
          .menu-link {
          justify-content: center;

          padding-left: 0 !important;
          padding-right: 0 !important;
        }

        html.layout-menu-collapsed
          #layout-menu.hostel-sidebar
          .menu-icon {
          margin-right: 0 !important;

          width: 28px;
          min-width: 28px;

          font-size: 20px;
        }

        html.layout-menu-collapsed
          #layout-menu.hostel-sidebar
          .menu-link
          > div {
          display: none;
        }

        /*
         * Make the collapsed active item visually stronger.
         */
        html.layout-menu-collapsed
          #layout-menu.hostel-sidebar
          .menu-item.active
          > .menu-link {
          border-radius: 10px;

          box-shadow:
            0 4px 10px rgba(
              var(--bs-primary-rgb),
              0.25
            );
        }


        /* =========================================================
           ACTIVE INDICATOR

           Removed in favour of the full main-colour highlight.
           This avoids having both a purple background and another
           purple vertical bar competing visually.
        ========================================================= */

        #layout-menu.hostel-sidebar
          .menu-item.active
          > .menu-link::before {
          display: none;
        }


        /* =========================================================
           DIVIDER
        ========================================================= */

        .hostel-menu-divider {
          pointer-events: none;

          margin-top: 7px !important;
          margin-bottom: 7px !important;
        }

        .menu-divider-line {
          height: 1px;

          margin: 0 8px;

          background: var(--bs-border-color);
        }

        html.layout-menu-collapsed
          #layout-menu.hostel-sidebar
          .menu-divider-line {
          margin-left: 5px;
          margin-right: 5px;
        }


        /* =========================================================
           SCROLLBAR
        ========================================================= */

        #layout-menu.hostel-sidebar {
          scrollbar-width: thin;

          scrollbar-color:
            rgba(var(--bs-primary-rgb), 0.18)
            transparent;
        }

        #layout-menu.hostel-sidebar::-webkit-scrollbar {
          width: 4px;
        }

        #layout-menu.hostel-sidebar::-webkit-scrollbar-track {
          background: transparent;
        }

        #layout-menu.hostel-sidebar::-webkit-scrollbar-thumb {
          background: rgba(
            var(--bs-primary-rgb),
            0.18
          );

          border-radius: 10px;
        }


        /* =========================================================
           TOOLTIP-LIKE LABELS FOR COLLAPSED SIDEBAR

           Native title attributes are also provided in JSX.
           This keeps the implementation simple and reliable.
        ========================================================= */

        html.layout-menu-collapsed
          #layout-menu.hostel-sidebar
          .menu-link {
          position: relative;
        }


        /* =========================================================
           RESPONSIVE
        ========================================================= */

        @media (max-width: 1199px) {
          #layout-menu.hostel-sidebar {
            width: 80px;
          }

          html:not(.layout-menu-collapsed)
            #layout-menu.hostel-sidebar {
            width: 260px;

            box-shadow:
              6px 0 24px rgba(0, 0, 0, 0.12);
          }
        }

        @media (max-width: 575px) {
          html:not(.layout-menu-collapsed)
            #layout-menu.hostel-sidebar {
            width: min(280px, 86vw);
          }
        }
      `}</style>

      <aside
        id="layout-menu"
        className="layout-menu menu-vertical menu bg-menu-theme hostel-sidebar"
        aria-label="Hostel navigation"
      >
        {/* =========================================================
            SIDEBAR HEADER
        ========================================================= */}
        <div className="app-brand demo">
          <Link
            to="/hostel"
            className="hostel-brand"
            onClick={handleMenuClick}
            aria-label="Hostel Dashboard"
          >
            <span className="hostel-brand-icon">
              <i className="ri-building-4-line" />
            </span>

            <span className="hostel-brand-text">
              <span className="hostel-brand-title">Hostel</span>

              <span className="hostel-brand-subtitle">Management</span>
            </span>
          </Link>

          <button
            type="button"
            className="hostel-toggle-btn"
            onClick={toggleSidebar}
            aria-label={
              isExpanded
                ? "Collapse hostel navigation"
                : "Expand hostel navigation"
            }
            title={isExpanded ? "Collapse menu" : "Expand menu"}
          >
            <i className="ri-arrow-left-s-line" />
          </button>
        </div>

        <div className="menu-inner-shadow" />

        {/* =========================================================
            MENU
        ========================================================= */}
        <ul className="menu-inner py-1">
          {/* Main Dashboard */}
          {hasAccess("/") && (
            <li className={`menu-item ${pathname === "/" ? "active" : ""}`}>
              <Link
                to="/"
                className="menu-link"
                onClick={handleMenuClick}
                title="Main Dashboard"
              >
                <i className="ri-arrow-left-circle-line menu-icon tf-icons" />
                <div>Main Dashboard</div>
              </Link>
            </li>
          )}

          {/* Hostel Dashboard */}
          {hasAccess("/hostel") && (
            <li
              className={`menu-item ${pathname === "/hostel" ? "active" : ""}`}
            >
              <Link
                to="/hostel"
                className="menu-link"
                onClick={handleMenuClick}
                title="Dashboard"
              >
                <i className="ri-dashboard-3-line menu-icon tf-icons" />
                <div>Dashboard</div>
              </Link>
            </li>
          )}

          {/* Students */}
          {hasAccess("/hostel/students") && (
            <li
              className={`menu-item ${isActive("/hostel/students", "/hostel/profile") ? "active" : ""
                }`}
            >
              <Link
                to="/hostel/students"
                className="menu-link"
                onClick={handleMenuClick}
                title="Students"
              >
                <i className="ri-group-line menu-icon tf-icons" />
                <div>Students</div>
              </Link>
            </li>
          )}

          {/* Attendance */}
          {hasAccess("/hostel/student/attendance") && (
            <li
              className={`menu-item ${pathname === "/hostel/student/attendance" ? "active" : ""
                }`}
            >
              <Link
                to="/hostel/student/attendance"
                className="menu-link"
                onClick={handleMenuClick}
                title="Attendance"
              >
                <i className="ri-calendar-check-line menu-icon tf-icons" />
                <div>Attendance</div>
              </Link>
            </li>
          )}

          {/* Fees Deposit */}
          {hasAccess("/hostel/fees/deposit") && (
            <li
              className={`menu-item ${isActive("/hostel/fees/deposit", "/student/fees/deposit")
                ? "active"
                : ""
                }`}
            >
              <Link
                to="/hostel/fees/deposit"
                className="menu-link"
                onClick={handleMenuClick}
                title="Fees Deposit"
              >
                <i className="ri-money-rupee-circle-line menu-icon tf-icons" />
                <div>Fees Deposit</div>
              </Link>
            </li>
          )}

          {/* Rooms & Beds */}
          {hasAccess("/hostel/rooms") && (
            <li
              className={`menu-item ${pathname === "/hostel/rooms" ? "active" : ""
                }`}
            >
              <Link
                to="/hostel/rooms"
                className="menu-link"
                onClick={handleMenuClick}
                title="Rooms & Beds"
              >
                <i className="ri-hotel-bed-line menu-icon tf-icons" />
                <div>Rooms &amp; Beds</div>
              </Link>
            </li>
          )}

          {/* ID Card */}
          {hasAccess("/hostel/student/id-card") && (
            <li
              className={`menu-item ${pathname === "/hostel/student/id-card" ? "active" : ""
                }`}
            >
              <Link
                to="/hostel/student/id-card"
                className="menu-link"
                onClick={handleMenuClick}
                title="ID Card"
              >
                <i className="ri-id-card-line menu-icon tf-icons" />
                <div>ID Card</div>
              </Link>
            </li>
          )}

          {/* Student Transfer */}
          {hasAccess("/hostel/student/transfer") && (
            <li
              className={`menu-item ${pathname === "/hostel/student/transfer" ? "active" : ""
                }`}
            >
              <Link
                to="/hostel/student/transfer"
                className="menu-link"
                onClick={handleMenuClick}
                title="Student Transfer"
              >
                <i className="ri-user-shared-line menu-icon tf-icons" />
                <div>Student Transfer</div>
              </Link>
            </li>
          )}

          {/* Expenses */}
          {hasAccess("/hostel/expenses") && (
            <li
              className={`menu-item ${isActive("/hostel/expenses", "/hostel/expense/category")
                ? "active"
                : ""
                }`}
            >
              <Link
                to="/hostel/expenses"
                className="menu-link"
                onClick={handleMenuClick}
                title="Expenses"
              >
                <i className="ri-wallet-3-line menu-icon tf-icons" />
                <div>Expenses</div>
              </Link>
            </li>
          )}

          {/* Reports */}
          {[
            "/hostel/reports",
            "/hostel/credit/debit/report",
            "/hostel/fee/report",
            "/hostel/expense/report",
            "/hostel/fee/due/report",
            "/hostel/student/month/report",
          ].some((route) => hasAccess(route)) && (
              <li
                className={`menu-item ${isActive(
                  "/hostel/reports",
                  "/hostel/fee/report",
                  "/hostel/expense/report",
                  "/hostel/fee/due/report",
                  "/hostel/credit/debit/report",
                  "/hostel/student/month/report",
                )
                  ? "active"
                  : ""
                  }`}
              >
                <Link
                  to={
                    hasAccess("/hostel/reports")
                      ? "/hostel/reports"
                      : hasAccess("/hostel/credit/debit/report")
                        ? "/hostel/credit/debit/report"
                        : hasAccess("/hostel/fee/report")
                          ? "/hostel/fee/report"
                          : hasAccess("/hostel/expense/report")
                            ? "/hostel/expense/report"
                            : hasAccess("/hostel/fee/due/report")
                              ? "/hostel/fee/due/report"
                              : "/hostel/student/month/report"
                  }
                  className="menu-link"
                  onClick={handleMenuClick}
                  title="Reports"
                >
                  <i className="ri-bar-chart-2-line menu-icon tf-icons" />
                  <div>Reports</div>
                </Link>
              </li>
            )}

          {/* Banks */}
          {/* {hasAccess("/hostel/banks") && (
            <li
              className={`menu-item ${
                isActive("/hostel/banks", "/hostel/banks/fund/transfer")
                  ? "active"
                  : ""
              }`}
            >
              <Link
                to="/hostel/banks"
                className="menu-link"
                onClick={handleMenuClick}
                title="Banks Details"
              >
                <i className="ri-bank-line menu-icon tf-icons" />
                <div>Banks Details</div>
              </Link>
            </li>
          )} */}

          {/* Divider */}
          <li className="menu-item hostel-menu-divider">
            <div className="menu-divider-line" />
          </li>

          {/* User Help */}
          {/* <li
            className={`menu-item ${pathname === "/user/help" ? "active" : ""}`}
          >
            <Link
              to="/user/help"
              className="menu-link"
              onClick={handleMenuClick}
              title="User Help"
            >
              <i className="ri-customer-service-2-line menu-icon tf-icons" />
              <div>User Help</div>
            </Link>
          </li> */}

          {/* Logout */}
          <li className={`menu-item ${pathname === "/logout" ? "active" : ""}`}>
            <Link
              to="/logout"
              className="menu-link"
              onClick={handleMenuClick}
              title="Log Out"
            >
              <i className="ri-logout-circle-r-line menu-icon tf-icons text-danger" />
              <div>Log Out</div>
            </Link>
          </li>
        </ul>
      </aside>
    </>
  );
}
